import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Navigate } from 'react-router-dom';
import { completeRegistrationFromLead } from '../services/registrationsService';

/**
 * Public route: /complete-registration/:eventId?leadId=xxx&email=xxx
 * Validates lead + event + email via RPC, then redirects to the main registration page
 * with the same params so the user sees Steps 2-3 only with prefilled data.
 */
export default function CompleteRegistration() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const leadId = searchParams.get('leadId');
  const email = searchParams.get('email');

  const [status, setStatus] = useState('loading'); // 'loading' | 'redirect' | 'error'
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!eventId || !leadId || !email?.trim()) {
      setErrorMessage('Missing required parameters. Please use the link from your email (leadId and email are required).');
      setStatus('error');
      return;
    }

    let isActive = true;

    completeRegistrationFromLead(leadId, eventId, email.trim())
      .then(({ data, error }) => {
        if (!isActive) return;
        if (error || !data) {
          setErrorMessage(error?.message || 'Unable to load your registration. Please use the link from your email or start over.');
          setStatus('error');
          return;
        }
        setStatus('redirect');
      })
      .catch(() => {
        if (isActive) {
          setErrorMessage('Something went wrong. Please try again or use the link from your email.');
          setStatus('error');
        }
      });

    return () => { isActive = false; };
  }, [eventId, leadId, email]);

  if (status === 'redirect') {
    const params = new URLSearchParams();
    if (leadId) params.set('leadId', leadId);
    if (email?.trim()) params.set('email', email.trim());
    const query = params.toString();
    return (
      <Navigate
        to={`/register/${eventId}${query ? `?${query}` : ''}`}
        replace
      />
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Unable to load registration</h1>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <a
            href={eventId ? `/register/${eventId}` : '/'}
            className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {eventId ? 'Go to registration' : 'Go to home'}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600">Loading your registration...</p>
      </div>
    </div>
  );
}
